import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';

const UNIFORM_TYPES = {
  shirt: '👔 Shirt', pant: '👖 Pant', skirt: '👗 Skirt', tie: '🎀 Tie', belt: 'Belt',
  socks: '🧦 Socks', shoes: '👟 Shoes', blazer: '🧥 Blazer', sweater: '🧶 Sweater',
  pe_shirt: '🏃 PE Shirt', pe_pant: '🏃 PE Pant', other: 'Other'
};

const TeacherMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const res = await axios.get(`${API_BASE}/materials/my-class`);
        setMaterials(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchMaterials();
  }, []);

  const totalBooksCost = (books) => books.reduce((sum, b) => sum + (Number(b.price) || 0), 0);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1">
        <header className="bg-white shadow-sm border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📚 Class Books & Uniform</h1>
            <p className="text-sm text-gray-500">Required materials for your class students</p>
          </div>
          <ProfileDropdown />
        </header>

        <main className="p-6">
          {loading ? <p>Loading...</p> : materials.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-10 text-center">
              <div className="text-5xl mb-4">📚</div>
              <h3 className="text-lg font-semibold text-gray-700">No Materials Listed</h3>
              <p className="text-gray-500">Materials haven't been assigned for your class yet.</p>
            </div>
          ) : (
            materials.map(mat => (
              <div key={mat._id} className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
                <div className="px-6 py-4 bg-gradient-to-r from-indigo-500 to-blue-500">
                  <h3 className="font-bold text-lg text-white">{mat.classId?.className} - {mat.classId?.section}</h3>
                  <p className="text-sm text-blue-100">Academic Year: {mat.academicYear}</p>
                </div>

                <div className="p-6 grid md:grid-cols-2 gap-6">
                  {/* Books */}
                  <div>
                    <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                      📖 Books <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{mat.books.length}</span>
                    </h4>
                    {mat.books.length === 0 ? <p className="text-gray-400 text-sm">No books</p> : (
                      <div className="space-y-2">
                        {mat.books.map((b, i) => (
                          <div key={i} className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="font-medium text-gray-800 text-sm">{b.name}</p>
                              <p className="text-xs text-gray-500">{b.subject} {b.author && `• ${b.author}`}</p>
                            </div>
                            <div className="text-right">
                              <span className="font-semibold text-green-700 text-sm">₹{b.price || 0}</span>
                              {b.isRequired && <span className="block text-xs text-red-500">Required</span>}
                            </div>
                          </div>
                        ))}
                        <p className="text-right font-bold text-sm text-gray-700 pt-2 border-t">Total: ₹{totalBooksCost(mat.books)}</p>
                      </div>
                    )}
                  </div>

                  {/* Uniform */}
                  <div>
                    <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                      👔 Uniform & Shoes <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{mat.uniformItems.length}</span>
                    </h4>
                    {mat.uniformItems.length === 0 ? <p className="text-gray-400 text-sm">No uniform items</p> : (
                      <div className="space-y-2">
                        {mat.uniformItems.map((u, i) => (
                          <div key={i} className="bg-gray-50 rounded-lg px-3 py-2">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium text-gray-800 text-sm">{u.itemName} <span className="text-xs text-gray-500">({UNIFORM_TYPES[u.type] || u.type})</span></p>
                                <p className="text-xs text-gray-500">
                                  {u.gender === 'all' ? 'All' : u.gender === 'boys' ? '👦 Boys' : '👧 Girls'}
                                  {u.color && ` • ${u.color}`}
                                </p>
                              </div>
                              {u.isRequired && <span className="text-xs text-red-500">Required</span>}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {u.availableSizes.map((s, si) => (
                                <span key={si} className="text-xs bg-white border rounded px-2 py-0.5">
                                  {s.size} — ₹{s.price}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {mat.notes && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-gray-600 bg-yellow-50 rounded-lg p-3">📝 {mat.notes}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </main>
      </div>
    </div>
  );
};

export default TeacherMaterials;
